import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  Input,
  NativeSelect,
  Tooltip,
  useToast,
} from '@provenance/design';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, FileCheck, Paperclip, Plus, Sparkles, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { toBase64 } from '../configuration/upload';

interface Attachment {
  id: string;
  category: string;
  categoryLabel: string;
  categorySuggested: boolean;
  description: string | null;
  origin: string;
  filename: string;
  fileUrl: string | null;
  requestSignatureAndStamp: boolean;
  stampedFileUrl: string | null;
}

interface RequiredCheck {
  category: string;
  categoryLabel: string;
  status: 'SATISFIED' | 'MISSING' | 'OPTIONAL' | 'EXTRA';
  stampByDefault: boolean;
  note: string | null;
  filenames: string[];
}

interface RequiredOutcome {
  satisfied: boolean;
  checks: RequiredCheck[];
  missing: Array<{ category: string; categoryLabel: string }>;
}

/**
 * The attachments on a request, and what the chamber expected.
 *
 * Editable, which the list was not. Three things an operator needs and the channels cannot
 * supply:
 *
 *  • Email infers a category from a filename. The guess is often right and sometimes not,
 *    and it is marked as a guess until a human says otherwise — never presented as something
 *    the sender stated.
 *  • Whether a supporting document needs stamping is a regional rule the exporter may not
 *    know. Mandatory across the Middle East, common in Asia.
 *  • Documents arrive by paths the product does not own: a separate email, a fax, someone
 *    walking it in.
 *
 * Read-only once the request has been decided. The API refuses it either way; hiding the
 * controls means an operator is not offered something that will fail.
 */
export function AttachmentsPanel({
  documentId,
  attachments,
  editable,
}: {
  documentId: string;
  attachments: Attachment[];
  editable: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const required = useQuery({
    queryKey: ['required-documents', documentId],
    queryFn: () => api.get<RequiredOutcome>(`/api/console/documents/${documentId}/required-documents`),
  });
  const categories = useQuery({
    queryKey: ['supporting-categories'],
    queryFn: () => api.get<Array<{ code: string; label: string }>>('/api/console/documents/supporting-categories'),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['document', documentId] });
    void queryClient.invalidateQueries({ queryKey: ['required-documents', documentId] });
  };

  const update = useMutation({
    mutationFn: (input: { id: string; patch: Record<string, unknown> }) =>
      api.patch(`/api/console/documents/${documentId}/supporting/${input.id}`, input.patch),
    onSuccess: refresh,
    onError: (error) =>
      toast.error('Could not update', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  const missing = required.data?.missing ?? [];
  const suggested = attachments.filter((a) => a.categorySuggested);

  return (
    <div className="flex flex-col gap-4">
      {missing.length > 0 && (
        <Callout
          tone="warn"
          icon={<AlertTriangle className="size-4" />}
          title={
            missing.length === 1
              ? `This chamber usually asks for a ${missing[0]!.categoryLabel.toLowerCase()}`
              : `This chamber usually asks for ${missing.length} documents that are not here`
          }
        >
          {missing.map((m) => m.categoryLabel).join(', ')}. The request was still accepted — you can
          ask for them with a clarification, or attach them yourself if you have them.
        </Callout>
      )}

      {suggested.length > 0 && (
        <Callout
          tone="info"
          icon={<Sparkles className="size-4" />}
          title={
            suggested.length === 1
              ? '1 category was guessed from a filename'
              : `${suggested.length} categories were guessed from filenames`
          }
        >
          Email carries no field for what an attachment is, so it was inferred. Confirm or correct
          each one below.
        </Callout>
      )}

      {attachments.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Paperclip className="size-5" />}
            title="Nothing attached"
            description={
              editable
                ? 'No supporting documents arrived with this request. You can attach one you received another way.'
                : 'No supporting documents arrived with this request.'
            }
            action={
              editable ? (
                <Button variant="secondary" leadingIcon={<Plus className="size-3.5" />} onClick={() => setAdding(true)}>
                  Attach a document
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <Card padded={false}>
          <div className="px-5 pt-4">
            <CardHeader
              title="Attachments"
              description={
                editable
                  ? 'Correct a category, ask for one to be stamped, or attach a document that arrived another way.'
                  : 'This request has been decided, so attachments can no longer be changed.'
              }
            />
          </div>
          <ul className="flex list-none flex-col gap-0 p-0">
            {attachments.map((a) => (
              <li
                key={a.id}
                className="flex flex-wrap items-start gap-3 border-b border-[var(--pv-line)] px-5 py-4 last:border-b-0"
              >
                <Paperclip aria-hidden className="mt-1 size-4 shrink-0 text-[var(--pv-faint)]" />
                <div className="min-w-[200px] flex-1">
                  <p className="truncate [font-size:13.5px] font-medium">{a.filename}</p>
                  <p className="[font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                    {a.description ? `${a.description} · ` : ''}
                    {a.origin === 'ADDED'
                      ? 'Added by the chamber'
                      : a.origin === 'PROVIDED'
                        ? 'Provided in answer to a clarification'
                        : 'Submitted with the request'}
                  </p>
                </div>

                {editable ? (
                  <div className="flex min-w-[190px] flex-col gap-1">
                    <NativeSelect
                      aria-label={`Category for ${a.filename}`}
                      value={a.category}
                      onChange={(e) => update.mutate({ id: a.id, patch: { category: e.target.value } })}
                    >
                      {(categories.data ?? []).map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.label}
                        </option>
                      ))}
                    </NativeSelect>
                    {a.categorySuggested && (
                      <Button
                        variant="ghost"
                        size="sm"
                        leadingIcon={<Check className="size-3.5" />}
                        onClick={() => update.mutate({ id: a.id, patch: { category: a.category } })}
                      >
                        Confirm “{a.categoryLabel}”
                      </Button>
                    )}
                  </div>
                ) : (
                  <Badge tone="neutral" size="sm">
                    {a.categoryLabel}
                  </Badge>
                )}

                {editable ? (
                  <Checkbox
                    checked={a.requestSignatureAndStamp}
                    onCheckedChange={(v) =>
                      update.mutate({ id: a.id, patch: { requestSignatureAndStamp: v === true } })
                    }
                    label="Stamp too"
                  />
                ) : (
                  a.requestSignatureAndStamp && (
                    <Tooltip content="This file is stamped alongside the certificate">
                      <Badge tone={a.stampedFileUrl ? 'ok' : 'clay'} size="sm">
                        {a.stampedFileUrl ? 'Stamped' : 'To stamp'}
                      </Badge>
                    </Tooltip>
                  )
                )}

                <Button variant="ghost" size="sm" asChild>
                  <a href={a.stampedFileUrl ?? a.fileUrl ?? '#'} target="_blank" rel="noreferrer">
                    Open
                  </a>
                </Button>
              </li>
            ))}
          </ul>

          {editable && (
            <div className="border-t border-[var(--pv-line)] px-5 py-3.5">
              <Button variant="secondary" size="sm" leadingIcon={<Plus className="size-3.5" />} onClick={() => setAdding(true)}>
                Attach a document
              </Button>
            </div>
          )}
        </Card>
      )}

      {required.data && required.data.checks.length > 0 && (
        <Card>
          <CardHeader
            title="What this chamber asks for"
            description="Configured per certificate type. A missing document is a warning, never a refusal."
          />
          <ul className="flex list-none flex-col gap-0 p-0">
            {required.data.checks.map((c) => (
              <li
                key={c.category}
                className="flex flex-wrap items-center gap-3 border-b border-[var(--pv-line)] px-5 py-3 last:border-b-0"
              >
                <Badge
                  tone={c.status === 'SATISFIED' ? 'ok' : c.status === 'MISSING' ? 'warn' : 'neutral'}
                  size="sm"
                >
                  {c.status === 'SATISFIED'
                    ? 'Have it'
                    : c.status === 'MISSING'
                      ? 'Missing'
                      : c.status === 'OPTIONAL'
                        ? 'Optional'
                        : 'Extra'}
                </Badge>
                <div className="min-w-0 flex-1">
                  <p className="[font-size:13px] font-medium">{c.categoryLabel}</p>
                  {(c.note || c.filenames.length > 0) && (
                    <p className="[font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                      {c.filenames.length > 0 ? c.filenames.join(', ') : c.note}
                    </p>
                  )}
                </div>
                {c.stampByDefault && (
                  <Tooltip content="This chamber normally stamps this document as well as the certificate">
                    <Badge tone="leaf" size="sm">
                      Normally stamped
                    </Badge>
                  </Tooltip>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {adding && (
        <AddAttachment
          documentId={documentId}
          categories={categories.data ?? []}
          suggestedCategory={missing[0]?.category ?? null}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            refresh();
            toast.success('Attached', 'Recorded as added by the chamber, not submitted by the exporter.');
          }}
        />
      )}
    </div>
  );
}

function AddAttachment({
  documentId,
  categories,
  suggestedCategory,
  onClose,
  onAdded,
}: {
  documentId: string;
  categories: Array<{ code: string; label: string }>;
  suggestedCategory: string | null;
  onClose: () => void;
  onAdded: () => void;
}) {
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState(suggestedCategory ?? categories[0]?.code ?? '');
  const [description, setDescription] = useState('');
  const [stamp, setStamp] = useState(false);

  const add = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a file first');
      return api.post(`/api/console/documents/${documentId}/supporting`, {
        category,
        description: description.trim() || null,
        filename: file.name,
        contentBase64: await toBase64(file),
        contentType: file.type || 'application/pdf',
        requestSignatureAndStamp: stamp,
      });
    },
    onSuccess: onAdded,
    onError: (error) =>
      toast.error('Could not attach', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  return (
    <Card>
      <CardHeader
        title="Attach a document"
        description="For something that reached you another way — a separate email, a fax, a hand delivery. Recorded as added by the chamber so the record still shows what the exporter sent."
      />
      <div className="flex flex-col gap-4 px-5 pb-5">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <Button variant="secondary" leadingIcon={<Upload className="size-3.5" />} onClick={() => fileInput.current?.click()}>
            {file ? 'Choose a different file' : 'Choose a file'}
          </Button>
          {file && (
            <span className="[font-size:12.5px] text-[var(--pv-muted)]">
              {file.name} · {Math.round(file.size / 1024)} KB
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px]">
            <NativeSelect aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="min-w-[220px] flex-1">
            <Input
              placeholder="Where it came from (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <Checkbox checked={stamp} onCheckedChange={(v) => setStamp(v === true)} label="Stamp this too" />
        </div>

        <div className="flex gap-2">
          <Button
            variant="primary"
            leadingIcon={<FileCheck className="size-3.5" />}
            disabled={!file || !category}
            loading={add.isPending}
            onClick={() => add.mutate()}
          >
            Attach
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}
